import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../components/ui/AnimatedSection';
import { Shield, Leaf, Star, Users, Lock, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Engagements() {
  const { t } = useTranslation();
  // Base commitments data with icons, merged with translated text
  const baseCommitments = [
    { icon: <EyeOff strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> },
    { icon: <Leaf strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> },
    { icon: <Star strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> },
    { icon: <Users strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> },
    { icon: <Lock strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> },
    { icon: <Shield strokeWidth={1} className="w-10 h-10 mb-6 text-gold-500" /> }
  ];

  const translatedCommitments = t('engagements.commitments', { returnObjects: true }) as Array<{ title: string; desc: string }>;
  const commitments = baseCommitments.map((item, idx) => ({ ...item, ...translatedCommitments[idx] }));

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-cream-100">
      <Navbar />
      
      <main className="flex-grow">
        {/* HERO SECTION */}
        <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] ease-out hover:scale-110"
            style={{ backgroundImage: 'url("/assets/luxury_gloves.webp")' }}
          >
            <div className="absolute inset-0 bg-black-deep/70"></div>
          </div>
          <FadeIn className="relative z-10 text-center px-4 mt-20">
            <span className="text-gold-400 font-serif italic text-xl md:text-2xl mb-4 block tracking-wide">
              {t('engagements.heroTag')}
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-white leading-tight drop-shadow-lg">
              {t('engagements.heroTitle1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 italic">{t('engagements.heroTitle2')}</span>
            </h1>
          </FadeIn>
        </div>

        {/* INTRODUCTION */}
        <section className="py-24 bg-white relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <FadeIn>
              <h2 className="text-3xl md:text-5xl font-serif text-black-deep mb-8 leading-tight">
                {t('engagements.introTitle')}
              </h2>
              <div className="w-16 h-px bg-gold-400 mx-auto mb-10"></div>
              <p 
                className="text-lg md:text-xl font-light text-gray-500 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: t('engagements.introDesc') }}
              ></p>
            </FadeIn>
          </div>
        </section>

        {/* GRID DES ENGAGEMENTS (Bento style) */}
        <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden">
          {/* Decorative glow */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gold-900/10 rounded-full blur-[150px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px] pointer-events-none"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <FadeInStagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {commitments.map((item, idx) => (
                <FadeInStaggerItem key={idx} className="h-full">
                  <div className="group h-full bg-[#0a0a0a] border border-white/10 p-10 md:p-12 rounded-2xl hover:border-gold-500/50 transition-all duration-500 hover:shadow-[0_0_40px_rgba(201,152,85,0.1)] relative overflow-hidden flex flex-col">
                    {/* Hover light effect */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gold-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
                    
                    <div className="relative z-10 transform group-hover:scale-110 group-hover:-translate-y-2 transition-transform duration-500 origin-left">
                      {item.icon}
                    </div>
                    
                    <h3 className="text-2xl font-serif text-white mb-4 relative z-10 group-hover:text-gold-400 transition-colors duration-300">
                      {item.title}
                    </h3>
                    
                    <div className="w-12 h-px bg-white/20 mb-6 group-hover:bg-gold-500/50 transition-colors duration-300"></div>
                    
                    <p className="text-gray-400 font-light text-sm md:text-base leading-relaxed relative z-10 flex-grow">
                      {item.desc}
                    </p>
                  </div>
                </FadeInStaggerItem>
              ))}
            </FadeInStagger>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-24 bg-white text-center">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <span className="text-gold-600 tracking-[0.2em] text-xs font-bold uppercase mb-4 block">{t('engagements.ctaTag')}</span>
              <h2 className="text-3xl md:text-5xl font-serif text-black-deep mb-8">
                {t('engagements.ctaTitle')}
              </h2>
              <p className="text-gray-500 font-light mb-12">
                {t('engagements.ctaDesc')}
              </p>
              <Link 
                to="/audit" 
                className="inline-block px-10 py-4 bg-black-deep text-white text-sm uppercase tracking-widest rounded-full shadow-lg hover:shadow-2xl hover:bg-gray-900 hover:-translate-y-1 transition-all duration-300"
              >
                {t('engagements.ctaBtn')}
              </Link>
            </FadeIn>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
